"use client";

import Script from "next/script";
import { useEffect, useState } from "react";
import { useReportWebVitals } from "next/web-vitals";

export const ANALYTICS_CONSENT_KEY = "elenails_analytics_consent";
export const ANALYTICS_CONSENT_EVENT = "elenails:analytics-consent";

const WEBSITE_ID = "f67c59af-782a-43d3-aa19-0b8373c78588";
const SCRIPT_SRC = "https://analytics.fxmp.org/script.js";

function shouldEnableAnalytics() {
  if (typeof window === "undefined") return false;
  if (window.navigator.doNotTrack === "1" || (window.navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl) return false;
  const consent = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
  if (consent === "accepted") return true;
  // Visitors who dismissed the previous informational banner had already
  // been counted by Umami; keep that choice during the migration.
  if (consent === null && window.localStorage.getItem("elenails_cookie_notice_seen") === "1") return true;
  return false;
}

type UmamiClient = {
  track: (event?: string | Record<string, unknown>, data?: Record<string, unknown>) => void;
};

declare global {
  interface Window { umami?: UmamiClient }
}

const reportMetric = (metric: { name: string; value: number; rating?: string; navigationType?: string }) => {
  if (!shouldEnableAnalytics() || !window.umami) return;
  const value = Math.round(metric.name === "CLS" ? metric.value * 1000 : metric.value);
  window.umami.track("web-vital", {
    metric: metric.name,
    value,
    rating: metric.rating || "unknown",
    navigationType: metric.navigationType || "unknown",
  });
};

export function UmamiAnalytics() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const sync = () => setEnabled(shouldEnableAnalytics());
    sync();
    window.addEventListener(ANALYTICS_CONSENT_EVENT, sync);
    return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, sync);
  }, []);

  useReportWebVitals(reportMetric);

  if (!enabled) return null;
  return (
    <Script
      id="umami-analytics"
      src={SCRIPT_SRC}
      data-website-id={WEBSITE_ID}
      data-domains="elenails-hair.fxmp.org"
      data-do-not-track="true"
      data-exclude-search="true"
      data-exclude-hash="true"
      data-auto-track="true"
      strategy="afterInteractive"
    />
  );
}
